import React from 'react';
import { IconBorder, IconDisabled, IconImage, IconLink } from '@bynder/icons';
import { useDebouncedCallback } from 'use-debounce';
import {
  List,
  Form,
  Input,
  Checkbox,
  Inspector,
  Thumbnail,
} from '@bynder/design-system';
import { Translate, useTranslate } from '@bynder/localization';
import { useActiveWidget } from '../../../../queries/guides';
import { CVAsset, CVFile, ImageWidget, WIDGET_TYPE } from '../../../../types';
import { useImageWidgetMutations } from '../../../../mutations/widgets/image';
import useImageSize from '../../../../hooks/useImageSize';
import { ImageActions, getAssetUrl } from '../../../../constants/widgets';
import { WidgetSettingsHeader } from '../../Styles';
import { useInspectorStore } from '../../../../stores/inspectorStore';
import ImageInspectorPanelSelector from './ImageInspectorPanelSelector';
import { InspectorSelect } from '../InspectorSelect';
import { UploadType } from '../../../../hooks/useUploader';
import { CSS_PROPERTIES, INSPECTOR_DEBOUNCE_DELAY } from '../constants';
import { SettingsAccordion } from '../SettingsAccordion';
import InspectorBorderRadius from '../InspectorBorderRadius';

import { isDevelopment } from '../../../../constants/app';
import CustomCssStyleEditor from '../CustomCSSEditor';
import { useWidgetStyleProperty } from '../helpers';

export const ImageWidgetSettings: React.FC = () => {
  const widget = useActiveWidget() as ImageWidget;
  const widgetSettings = useInspectorStore(s => s.widgetSettings);
  const { editStyle } = useImageWidgetMutations(
    widgetSettings.sectionId,
    widgetSettings.columnIndex,
    widgetSettings.widgetId,
  );
  return (
    <>
      <WidgetSettingsHeader widgetType={WIDGET_TYPE.IMAGE} />
      <ImageAssetSelector />
      <Inspector.Divider />
      <ImageActionSelector />
      <Inspector.Divider />
      <ImageBorderRadius />

      {isDevelopment && (
        <>
          <Inspector.Divider />
          <CustomCssStyleEditor
            handleChange={() => editStyle.mutate({ style: '' })}
            label="Section CSS"
            placeholder="Enter your custom CSS here"
            style={widget?.style?.stylesheet}
          />
        </>
      )}
    </>
  );
};

const ImageAssetSelector = () => {
  const widgetSettings = useInspectorStore(s => s.widgetSettings);
  const { updateImageWidget, uploadImageWidget } = useImageWidgetMutations(
    widgetSettings.sectionId,
    widgetSettings.columnIndex,
    widgetSettings.widgetId,
  );

  const widget = useActiveWidget() as ImageWidget;

  const [imageWidth, imageHeight] = useImageSize(widget?.imageUrl);

  const addImageAsset = (selectedMedia: CVAsset[], file: CVFile) => {
    const {
      databaseId,
      name,
      url: imageAssetUrl,
      extensions,
      derivatives,
    } = selectedMedia[0];
    updateImageWidget.mutate({
      widgetAssetExtension: extensions[0],
      widgetAssetId: databaseId,
      widgetAssetName: name,
      widgetDetailUrl: imageAssetUrl,
      widgetImageUrl: getAssetUrl(extensions[0], file, derivatives.webImage),
    });
  };

  const clearImage = () => {
    updateImageWidget.mutate({
      widgetAssetExtension: '',
      widgetAssetId: '',
      widgetAssetName: '',
      widgetDetailUrl: '',
      widgetImageUrl: '',
    });
  };

  const handleUploadCompleted = (mediaId: string) => {
    uploadImageWidget.mutate({
      sectionId: widgetSettings.sectionId,
      columnIndex: widgetSettings.columnIndex,
      assetId: mediaId,
      widgetId: widgetSettings?.widgetId,
    });
  };

  const setImage = (url: string) => {
    updateImageWidget.mutate({
      widgetAssetId: widget?.assetId,
      widgetAssetName: widget?.assetName,
      widgetDetailUrl: widget?.detailUrl,
      widgetAssetExtension: widget?.assetExtension,
      widgetImageUrl: url,
    });
  };

  return (
    <ImageInspectorPanelSelector
      uploadType={UploadType.Image}
      handleAssetSelect={addImageAsset}
      selectedAssetId={widget?.id}
      handleOnClearImage={clearImage}
      handleOnSetImage={setImage}
      imageUrl={widget?.imageUrl}
      assetName={widget?.assetName}
      imageWidth={imageWidth}
      imageHeight={imageHeight}
      handleUploadCompleted={handleUploadCompleted}
    />
  );
};

const ImageActionSelector = () => {
  const widget = useActiveWidget() as ImageWidget;
  const widgetSettings = useInspectorStore(s => s.widgetSettings);
  const { setImageAction } = useImageWidgetMutations(
    widgetSettings.sectionId,
    widgetSettings.columnIndex,
    widgetSettings.widgetId,
  );

  const isLinkToUrl = widget?.imageAction?.type === ImageActions.LINK_TO_URL;
  const items = [
    {
      label: <Translate id="IMAGE_WIDGET_ACTIONS.PREVIEW_ITEM" />,
      icon: <IconImage />,
      key: 0,
      value: ImageActions.OPEN_PREVIEW,
    },

    {
      label: <Translate id="IMAGE_WIDGET_ACTIONS.NO_ACTION_ITEM" />,
      icon: <IconDisabled />,
      key: 1,
      value: ImageActions.NO_ACTION,
    },

    {
      label: <Translate id="IMAGE_WIDGET_ACTIONS.ADD_LINK" />,
      icon: <IconLink />,
      key: 2,
      value: ImageActions.LINK_TO_URL,
    },
  ];

  const handleChange = (imageAction: ImageActions) => {
    setImageAction.mutate({
      imageAction,
    });
  };

  return (
    <>
      <Form.Group>
        <Form.Label id="imageAction">
          <Translate id="WIDGETS_INSPECTOR.IMAGE_ACTION_TITLE" />
        </Form.Label>
        <InspectorSelect
          items={items}
          selectedValue={widget?.imageAction?.type}
          handleChange={handleChange}
        />
      </Form.Group>

      {isLinkToUrl && <ImageUrlSettings />}
    </>
  );
};

const ImageUrlSettings = () => {
  const widget = useActiveWidget() as ImageWidget;
  const [imageUrl, setImageUrl] = React.useState(widget?.imageAction?.url);
  const [openNewTab, setOpenNewTab] = React.useState(
    widget?.imageAction?.openNewTab,
  );
  const widgetSettings = useInspectorStore(s => s.widgetSettings);
  const { setImageAction } = useImageWidgetMutations(
    widgetSettings.sectionId,
    widgetSettings.columnIndex,
    widgetSettings.widgetId,
  );

  const handleEditImageLink = () => {
    setImageAction.mutate({
      imageAction: ImageActions.LINK_TO_URL,
      imageActionUrl: imageUrl || '',
      imageActionNewTab: widget?.imageAction.openNewTab,
    });
  };

  const handleOpenInNewTab = (openInNewTab: boolean) => {
    setImageAction.mutate({
      imageAction: ImageActions.LINK_TO_URL,
      imageActionUrl: widget?.imageAction?.url || '',
      imageActionNewTab: openInNewTab,
    });
  };
  const debouncedSetUrl = useDebouncedCallback(
    handleEditImageLink,
    INSPECTOR_DEBOUNCE_DELAY,
  );
  React.useEffect(() => {
    setImageUrl(widget?.imageAction?.url);
    setOpenNewTab(widget?.imageAction?.openNewTab);
  }, [
    widget?.imageAction?.type,
    widget?.imageAction?.url,
    widget?.imageAction?.openNewTab,
  ]);

  const handleImageUrl = (newUrl: string) => {
    setImageUrl(newUrl);
    debouncedSetUrl();
  };

  return (
    <>
      <List.Item>
        <Input
          onBlur={handleEditImageLink}
          value={imageUrl}
          onChange={handleImageUrl}
          type="text"
          placeholder="https://example.com"
        />
      </List.Item>
      <List.Item>
        <Checkbox
          isChecked={openNewTab}
          onChange={() => handleOpenInNewTab(!openNewTab)}
        >
          <Translate id="WIDGETS_INSPECTOR.IMAGE_NEW_TAB" />
        </Checkbox>
      </List.Item>
    </>
  );
};

const ImageBorderRadius: React.FC = () => {
  const widget = useActiveWidget() as ImageWidget;
  const { translate } = useTranslate();
  const widgetSettings = useInspectorStore(s => s.widgetSettings);
  const { editStyle } = useImageWidgetMutations(
    widgetSettings.sectionId,
    widgetSettings.columnIndex,
    widgetSettings.widgetId,
  );
  const handleStyleChange = (style: string) => {
    editStyle.mutate({ style });
  };

  const { value: borderRadius, origin: borderRadiusOrigin } =
    useWidgetStyleProperty(
      widget?.style?.stylesheet,
      'widgets.media.borderRadius',
      CSS_PROPERTIES.BORDER_RADIUS,
    );

  return (
    <SettingsAccordion
      noDivider
      thumbnail={<Thumbnail icon={<IconBorder />} />}
      title={translate('WIDGETS_INSPECTOR.BORDER_RADIUS')}
    >
      <InspectorBorderRadius
        style={widget?.style?.stylesheet}
        handleStyleChange={handleStyleChange}
        borderRadius={borderRadius}
        origin={borderRadiusOrigin}
      />
    </SettingsAccordion>
  );
};
